import React, { useMemo } from 'react';
import './App.css';
import heroImage from './hero-image.jpg';
import { useLanguage } from './i18n/LanguageContext';

function App() {
  const { language, setLanguage, t } = useLanguage();

  const benefits = useMemo(
    () => [t('benefits.items.0'), t('benefits.items.1'), t('benefits.items.2')],
    [t]
  );
  const testimonials = useMemo(
    () => [t('testimonials.items.0'), t('testimonials.items.1')],
    [t]
  );

  return (
    <div className="page">
      <header className="top-nav">
        <a className="brand" href="#top">
          Moonlight Dreams
        </a>
        <nav className="top-links" aria-label={t('header.navAriaLabel')}>
          <a href="#features">{t('header.links.features')}</a>
          <a href="#benefits">{t('header.links.benefits')}</a>
          <a href="#reviews">{t('header.links.reviews')}</a>
          <a href="#contact">{t('header.links.support')}</a>
        </nav>
        <div className="top-actions">
          <div className="language-switch" role="group" aria-label={t('header.languageAriaLabel')}>
            <button
              type="button"
              className={language === 'en' ? 'lang-btn active' : 'lang-btn'}
              onClick={() => setLanguage('en')}
            >
              EN
            </button>
            <button
              type="button"
              className={language === 'es' ? 'lang-btn active' : 'lang-btn'}
              onClick={() => setLanguage('es')}
            >
              ES
            </button>
          </div>
          <a
            className="pill-button"
            href="https://apps.apple.com/app/id6744646285"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('header.getStarted')}
          </a>
        </div>
      </header>

      <main>
        <section className="hero" id="top">
          <div className="hero-copy">
            <h1>{t('hero.title')}</h1>
            <p>{t('hero.subtitle')}</p>
            <div className="hero-cta">
              <a
                className="cta cta-primary"
                href="https://apps.apple.com/app/id6744646285"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('hero.primaryCta')}
              </a>
              <a className="cta cta-secondary" href="#features">
                {t('hero.secondaryCta')}
              </a>
            </div>
          </div>
          <div className="hero-art-card">
            <img src={heroImage} alt={t('hero.imageAlt')} />
          </div>
        </section>

        <section className="intro" id="features">
          <h2>{t('intro.title')}</h2>
          <p>{t('intro.body')}</p>
        </section>

        <section className="feature-grid">
          <article className="feature feature-main">
            <span className="feature-label">{t('features.library.label')}</span>
            <h3>{t('features.library.title')}</h3>
            <p>{t('features.library.body')}</p>
          </article>
          <article className="feature">
            <h3>{t('features.nightMode.title')}</h3>
            <p>{t('features.nightMode.body')}</p>
          </article>
          <article className="feature">
            <h3>{t('features.adaptiveReading.title')}</h3>
            <p>{t('features.adaptiveReading.body')}</p>
          </article>
        </section>

        <section className="benefits-wrap" id="benefits">
          <h2>{t('benefits.title')}</h2>
          <div className="benefits">
            {benefits.map((benefit) => (
              <article className="benefit-card" key={benefit.title}>
                <h3>{benefit.title}</h3>
                <p>{benefit.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="testimonials-wrap" id="reviews">
          <h2>{t('testimonials.title')}</h2>
          <div className="testimonials">
            {testimonials.map((item) => (
              <article className="testimonial-card" key={item.name}>
                <p>"{item.quote}"</p>
                <span>
                  {item.name} · {item.role}
                </span>
              </article>
            ))}
          </div>
        </section>

        <section className="final-cta" id="support">
          <h2>{t('finalCta.title')}</h2>
          <p>{t('finalCta.body')}</p>
          <a
            className="cta cta-primary final-cta-button"
            href="https://apps.apple.com/app/id6744646285"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('finalCta.button')}
          </a>
        </section>

        <section className="contact-wrap" id="contact">
          <h2>{t('contact.title')}</h2>
          <p>{t('contact.body')}</p>
          <form
            className="contact-form"
            action="https://formspree.io/f/xrbqbdez"
            method="POST"
          >
            <label htmlFor="contact-name" className="sr-only">
              {t('contact.nameLabel')}
            </label>
            <input
              id="contact-name"
              name="name"
              type="text"
              placeholder={t('contact.namePlaceholder')}
              required
            />
            <label htmlFor="contact-email" className="sr-only">
              {t('contact.emailLabel')}
            </label>
            <input
              id="contact-email"
              name="email"
              type="email"
              placeholder={t('contact.emailPlaceholder')}
              required
            />
            <label htmlFor="contact-message" className="sr-only">
              {t('contact.messageLabel')}
            </label>
            <textarea
              id="contact-message"
              name="message"
              rows="5"
              placeholder={t('contact.messagePlaceholder')}
              required
            />
            <button className="contact-submit" type="submit">
              {t('contact.submit')}
            </button>
          </form>
        </section>
      </main>

      <footer className="footer">
        <div>
          <strong>Moonlight Dreams</strong>
          <p>{t('footer.copyright')}</p>
        </div>
        <div className="footer-links">
          <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">
            {t('footer.privacyPolicy')}
          </a>
        </div>
      </footer>
    </div>
  );
}

export default App;
